import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import loadable from '@loadable/component';
import queryString from 'query-string';
import cookie from 'react-cookie';
import config from 'config';
import Grid from 'react-bootstrap/lib/Grid';
import { ChevronDown, X } from 'lucide-react';
import { fromSeoUrl, toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import { trackEvent } from 'helpers/Analytics';

// Components
import JobsFilterPanel from 'components/JobsFilterPanel/JobsFilterPanel';
import getAppliedFilterCount from 'components/JobsFilterPanel/getAppliedFilterCount';
import getFilterSummary from 'components/JobsFilterPanel/getFilterSummary';
import {
  DEFAULT_EXPERIENCE_LEVELS,
  toggleExperienceGroup,
} from 'components/JobsFilterPanel/experienceLevelOptions';
import clearJobsFilters, {
  BRIEFS_FILTER_COOKIE_NAME,
} from 'components/JobsFilterPanel/clearJobsFilters';
import parseJobsStatus from 'components/JobsFilterPanel/parseJobsStatus';
import BriefListings from 'components/BriefListings/BriefListings';
import BrowseMoreJobs from 'components/BrowseMoreJobs/BrowseMoreJobs';
import JobsResultsHeader from 'components/JobsResultsHeader/JobsResultsHeader';
import Loader from 'components/Loader/Loader';
import ProjectsHeader from './ProjectsHeader/ProjectsHeader';
import { getRoleCategory } from 'helpers/Constants';
import hasUrlFilterParams from './hasUrlFilterParams';
import {
  BEST_MATCH_ORDER,
  buildJobsOrderUrl,
  getBestMatchRedirect,
  isSavedForUser,
} from './jobsOrder';
import {
  AUTO_LOAD_BATCH_WINDOW,
  MAX_URL_PAGE,
  buildJobsBatchUrl,
  clampJobsPage,
  loadBriefsThroughPage,
  parseJobsPage,
} from './jobsPagination';
import { getExternalJobsRequest } from './boardExternalJobs';
import {
  EXTERNAL_DAYS_PER_LOAD,
  SSR_TIME_ZONE,
  buildJobsTimeline,
  dayKey,
  getVisitorTimeZone,
  hasMoreExternalDays,
} from './jobsTimeline';
import {
  buildExternalJobsFilterParams,
  getExternalJobsKey,
} from 'redux/modules/externalJobs';
import layoutStyles from './ProjectsLayout.module.scss';

// Best match runs the full Ari flow inline; only fetch it when chosen.
// Fallback sits where Ari's own boot loader appears (48px down) to avoid a jump.
const Ari = loadable(() => import('containers/Ari/Ari'), {
  fallback: (
    <Loader
      loading
      style={{ marginTop: 48 }}
      aria-busy="true"
      aria-live="polite"
      data-testid="ari-chunk-loader"
    />
  ),
});

export { BEST_MATCH_ORDER, buildJobsOrderUrl };

const getOrderParam = (search = '') => queryString.parse(search).order || null;

const FILTER_INPUT_DEBOUNCE_MS = 500;

export default class Projects extends PureComponent {
  static propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,

    projects: PropTypes.object,
    suggestedRoles: PropTypes.object,
    suggestedSkills: PropTypes.object,

    roleEntities: PropTypes.object,
    skillEntities: PropTypes.object,
    briefsEntities: PropTypes.object,
    userEntities: PropTypes.object,

    loadBriefs: PropTypes.func,
    loadExternalJobDays: PropTypes.func,
    loadMoreExternalJobs: PropTypes.func,
    retryExternalJobDays: PropTypes.func,
    externalJobs: PropTypes.object,
    loadFilterRoleSkills: PropTypes.func,
    loadOwnBriefs: PropTypes.func,
    loadOwnPitches: PropTypes.func,
    loadSuggestedRoles: PropTypes.func,
    loadSuggestedSkills: PropTypes.func,

    posting: PropTypes.bool,
    posted: PropTypes.object,
    postBrief: PropTypes.func,
    clearPostedBrief: PropTypes.func,

    user: PropTypes.object,
    history: PropTypes.object,
    country: PropTypes.string,
    city: PropTypes.string,
    getCitiesForCountry: PropTypes.func,
    cities: PropTypes.object,

    loadingPartners: PropTypes.bool,
    loadedPartners: PropTypes.bool,
    loadPartners: PropTypes.func,
    partners: PropTypes.array,

    setSelectedRoleCategory: PropTypes.func,
    clearSelectedRoleCategory: PropTypes.func,
  };

  // Component Life-cycle
  constructor(props) {
    super(props);

    this.locationDebounceTimer = null;
    this.scrollListenerAttached = false;
    this.lastSyncedPage = null;

    const initialPage = parseJobsPage(props.location.search);

    this.state = {
      filter: {
        name: props.match.params.role ?? '',
        selectedRole: null,
        selectedRoles: props.match.params.role ? [props.match.params.role] : [],
        status: true,
        remote: false,
        internal: false,
        locationText: null,
        country: props.match.params.country ?? null,
        city: props.match.params.city ?? null,
        searchTerm: null,
        experienceLevels: DEFAULT_EXPERIENCE_LEVELS.map((level) => ({
          ...level,
        })),
        hiringCapacities: [
          { type: 'commercial', isChecked: false, label: 'Commercial' },
          { type: 'personal', isChecked: false, label: 'Personal' },
        ],
      },
      paid: props.location.pathname.includes('/jobs'),
      showFilterSheet: false,
      searchTermInput: '',
      experienceLevelsHaveChanged: false,
      hiringCapacitiesHaveChanged: false,
      rolesHaveChanged: false,
      testingPartners: props.location.query.testing ?? false,
      initialPage,
      autoLoadUntilPage: initialPage + AUTO_LOAD_BATCH_WINDOW - 1,
      scrollAutoLoadEnabled: true,
      emptyFocusChipKey: null,
      // Days group in the SSR zone until the visitor's zone has its data,
      // so the first HTML and the hydrated page match.
      visitorTimeZone: null,
      timelineTimeZone: SSR_TIME_ZONE,
      // Per filter hash: days revealed past the Twine jobs, expanded days.
      externalView: { hash: null, daysBack: 0, expanded: {} },
    };
  }

  // The order rides in the same cookie as the filters so a returning
  // visitor lands on Best match if that's what they last used. The user id
  // stops another account on this browser picking them up.
  updateCookie = (filters) => {
    const { user } = this.props;
    const expires = new Date();
    expires.setDate(expires.getDate() + 7); // 7 days
    cookie.save(
      BRIEFS_FILTER_COOKIE_NAME,
      {
        ...filters,
        order: this.isBestMatch() ? BEST_MATCH_ORDER : null,
        userId: user ? user.id : null,
      },
      { path: '/', expires }
    );
  };

  componentDidMount() {
    // Load Skills and Roles
    this.props.loadSuggestedRoles();
    this.props.loadSuggestedSkills();

    this.attachScrollListener();
    this.attachFilterSheetKeyListener();

    window.history.replaceState(
      { ...window.history.state, cameFromBackButton: false },
      document.title
    );

    const {
      projects,
      loadFilterRoleSkills,
      loadBriefs,
      loadOwnBriefs,
      loadOwnPitches,
      user,
      getCitiesForCountry,
      loadPartners,
      setSelectedRoleCategory,
      clearSelectedRoleCategory,
    } = this.props;

    const {
      paid,
      filter,
      filter: { experienceLevels, hiringCapacities },
      testingPartners,
      initialPage,
    } = this.state;

    loadPartners('jobs_board', testingPartners);

    this.setState({ visitorTimeZone: getVisitorTimeZone() });

    if (
      !projects.getIn(['filterRoleSkills', paid.toString(), 'loaded'], false) &&
      !projects.getIn(['filterRoleSkills', paid.toString(), 'loading'], false)
    ) {
      loadFilterRoleSkills(paid);
    }

    if (filter.selectedRoles && filter.selectedRoles.length > 0) {
      setSelectedRoleCategory(getRoleCategory(filter.selectedRoles[0]));
    } else {
      clearSelectedRoleCategory();
    }

    // Load Briefs — deep links (?page=N) load batches 1…N in order
    if (
      !projects.getIn(['briefs', this.getHash(), 'loaded'], false) &&
      !projects.getIn(['briefs', this.getHash(), 'loading'], false)
    ) {
      loadBriefsThroughPage(
        loadBriefs,
        initialPage,
        paid,
        filter.selectedRoles,
        experienceLevels,
        hiringCapacities,
        filter.status,
        filter.remote,
        filter.internal,
        filter.locationText,
        filter.searchTerm
      );
    } else {
      this.syncBatchUrlAndAutoLoad();
    }

    if (user && !this.props.match.params.role) {
      // URL filter params (e.g. bookmarks/shared links) take precedence over
      // the saved last-search cookie. Only restore the cookie on bare /jobs.
      if (hasUrlFilterParams(this.props.location.search)) {
        this.updateCookie(this.state.filter);
      } else {
        const filtersFromCookie = cookie.load(BRIEFS_FILTER_COOKIE_NAME);
        if (isSavedForUser(filtersFromCookie, user)) {
          // eslint-disable-next-line no-unused-vars
          const { order, userId, ...savedFilter } = filtersFromCookie;
          this.setState({
            filter: savedFilter,
            searchTermInput: savedFilter.searchTerm || '',
          });
          this.redirectToSavedBestMatch(filtersFromCookie);
        } else {
          if (!this.state.filter.status) {
            // Check if the user navigated to this page via the back button
            const cameFromBackButton =
              window.history.state && window.history.state.cameFromBackButton;
            if (!cameFromBackButton) {
              this.onStatusFilterToggle();
            }
          }

          if (user.featured_roles.length > 0 && !filter.rolesHaveChanged) {
            this.onRolesSelect(user.featured_roles, true);
          }
          this.updateCookie(this.state.filter);
        }
      }

      // Load Own Briefs
      if (
        !projects.getIn(['ownBriefs', 'loaded'], false) &&
        !projects.getIn(['ownBriefs', 'loading'], false)
      ) {
        loadOwnBriefs(user);
      }

      // Load Own Pitches
      if (
        !projects.getIn(['ownPitches', 'loaded'], false) &&
        !projects.getIn(['ownPitches', 'loading'], false)
      ) {
        loadOwnPitches(user);
      }
    }

    if (filter.country) {
      getCitiesForCountry(filter.country);
      return;
    }
  }

  // The server redirects a full page load; this covers navigating to /jobs
  // within the app, whether this page mounts or is already showing.
  redirectToSavedBestMatch = (saved) => {
    const { history, location, user } = this.props;
    const redirect = getBestMatchRedirect({
      pathname: location.pathname,
      search: location.search,
      saved,
      user,
    });
    if (redirect) {
      history.replace(redirect);
    }
    return Boolean(redirect);
  };

  componentWillUnmount() {
    this.detachScrollListener();
    this.detachFilterSheetKeyListener();
    clearTimeout(this.locationDebounceTimer);
    document.body.style.overflow = '';
    // Crisp sits above the sheet on production; restore it if we hid it.
    if (this.state.showFilterSheet) {
      this.setCrispChatVisible(true);
    }
    window.history.replaceState(
      { ...window.history.state, cameFromBackButton: true },
      document.title
    );
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.user &&
      getOrderParam(prevProps.location.search) !==
        getOrderParam(this.props.location.search)
    ) {
      this.updateCookie(this.state.filter);
    }

    // After the cookie update above, so leaving Best match doesn't bounce back.
    const { location } = this.props;
    if (
      this.props.user &&
      (prevProps.location.pathname !== location.pathname ||
        prevProps.location.search !== location.search)
    ) {
      const saved = cookie.load(BRIEFS_FILTER_COOKIE_NAME);
      if (this.redirectToSavedBestMatch(saved)) {
        // The order change saves the cookie again on the next update, so
        // restore its filters first or this page's filters replace them.
        // eslint-disable-next-line no-unused-vars
        const { order, userId, ...savedFilter } = saved;
        this.setState({
          filter: savedFilter,
          searchTermInput: savedFilter.searchTerm || '',
        });
      }
    }

    const hash = this.getHash();
    const pagesLoaded = this.getPagesLoaded();
    const prevPagesLoaded = prevProps.projects.getIn(
      ['briefs', hash, 'pagesLoaded'],
      0
    );

    if (pagesLoaded > 0 && pagesLoaded !== prevPagesLoaded) {
      this.syncBatchUrlAndAutoLoad();
    }

    this.syncExternalJobs();
  }

  // Jobs from other sites (Newest first timeline)
  getBoardFilters = () => {
    const { filter } = this.state;
    return {
      roles: filter.selectedRoles,
      experienceLevels: filter.experienceLevels,
      hiringCapacities: filter.hiringCapacities,
      remote: filter.remote,
      internal: filter.internal,
      jobLocationText: filter.locationText,
      searchTerm: filter.searchTerm,
    };
  };

  getExternalView = () => {
    const { externalView } = this.state;
    return externalView.hash === this.getHash()
      ? externalView
      : { hash: this.getHash(), daysBack: 0, expanded: {} };
  };

  getExternalEntry = (timeZone) =>
    (this.props.externalJobs || {})[
      getExternalJobsKey(this.getHash(), timeZone)
    ];

  // Only the fields the timeline groups by.
  getTimelineBriefs = () => {
    const { projects, briefsEntities } = this.props;
    return projects
      .getIn(['briefs', this.getHash(), 'briefs'], List())
      .map((id) => briefsEntities.get(id.toString()))
      .filter(Boolean)
      .map((brief) => ({
        approved_at: brief.get('approved_at'),
        timestamp: brief.get('timestamp'),
        boosted_expires_at: brief.get('boosted_expires_at'),
      }))
      .toArray();
  };

  getExternalRequest = (timeZone) =>
    getExternalJobsRequest({
      briefs: this.getTimelineBriefs(),
      moreToLoad: this.getMoreToLoad(),
      filters: this.getBoardFilters(),
      entry: this.getExternalEntry(timeZone),
      externalDaysBack: this.getExternalView().daysBack,
      timeZone,
    });

  syncExternalJobs = () => {
    const { visitorTimeZone, timelineTimeZone, paid } = this.state;
    const hash = this.getHash();
    if (
      !visitorTimeZone ||
      !paid ||
      this.isBestMatch() ||
      !this.props.projects.getIn(['briefs', hash, 'loaded'], false)
    ) {
      return;
    }

    const entry = this.getExternalEntry(visitorTimeZone);
    if (entry && entry.loading) {
      return;
    }
    // If the visitor's zone can't load, keep the server's days rather than
    // switching to a zone with no jobs from other sites.
    if (entry && entry.failed && visitorTimeZone !== SSR_TIME_ZONE) {
      this.setState({ visitorTimeZone: SSR_TIME_ZONE });
      return;
    }

    const range = this.getExternalRequest(visitorTimeZone);
    if (range) {
      this.props.loadExternalJobDays(
        getExternalJobsKey(hash, visitorTimeZone),
        buildExternalJobsFilterParams(this.getBoardFilters()),
        range,
        visitorTimeZone
      );
    } else if (timelineTimeZone !== visitorTimeZone) {
      this.setState({ timelineTimeZone: visitorTimeZone });
    } else if (this.isTimelineEmpty() && this.hasMoreExternal()) {
      // Nothing in the days shown so far: look further back rather than
      // showing no results above a "Load more jobs" link.
      this.revealMoreExternalDays();
    }
  };

  isTimelineEmpty = () => {
    const entry = this.getExternalEntry(this.state.timelineTimeZone);
    const days = (entry && entry.days) || {};
    return (
      this.getTimelineBriefs().length === 0 &&
      !Object.keys(days).some((date) => days[date].total > 0)
    );
  };

  onShowAllExternal = (date, offset) => {
    const zone = this.state.timelineTimeZone;
    const view = this.getExternalView();
    const day = (this.getExternalEntry(zone) || { days: {} }).days[date];

    this.setState({
      externalView: { ...view, expanded: { ...view.expanded, [date]: true } },
    });

    if (day && offset < day.total && !day.loading) {
      this.props.loadMoreExternalJobs(
        getExternalJobsKey(this.getHash(), zone),
        buildExternalJobsFilterParams(this.getBoardFilters()),
        date,
        zone,
        offset
      );
    }
  };

  getExternalFloorState = () => {
    const zone = this.state.timelineTimeZone;
    const timeline = buildJobsTimeline({
      briefs: this.getTimelineBriefs(),
      externalDays: {},
      moreToLoad: this.getMoreToLoad(),
      externalDaysBack: this.getExternalView().daysBack,
      today: dayKey(Date.now(), zone),
      timeZone: zone,
    });
    return timeline.floor;
  };

  // Once Twine jobs run out, older days of external jobs load a week at a time.
  hasMoreExternal = () => {
    if (
      this.getMoreToLoad() ||
      this.state.filter.internal ||
      !this.props.projects.getIn(['briefs', this.getHash(), 'loaded'], false)
    ) {
      return false;
    }
    const zone = this.state.timelineTimeZone;
    return hasMoreExternalDays(
      this.getExternalFloorState(),
      dayKey(Date.now(), zone)
    );
  };

  // The last fallback zone failed too, so some days are missing their jobs
  // from other sites until the visitor retries.
  hasExternalLoadFailed = () => {
    const { visitorTimeZone } = this.state;
    if (visitorTimeZone !== SSR_TIME_ZONE || this.state.filter.internal) {
      return false;
    }
    const entry = this.getExternalEntry(visitorTimeZone);
    return Boolean(entry && entry.failed && !entry.loading);
  };

  onRetryExternal = () => {
    const hash = this.getHash();
    const zone = getVisitorTimeZone();
    trackEvent('jobs-external-retry-clicked', { pageType: this.getPageType() });
    this.props.retryExternalJobDays(getExternalJobsKey(hash, SSR_TIME_ZONE));
    if (zone !== SSR_TIME_ZONE) {
      this.props.retryExternalJobDays(getExternalJobsKey(hash, zone));
    }
    this.setState({ visitorTimeZone: zone });
  };

  isExternalLoading = () => {
    const entry = this.getExternalEntry(this.state.timelineTimeZone);
    return Boolean(entry && entry.loading);
  };

  revealMoreExternalDays = () => {
    if (this.isExternalLoading()) {
      return;
    }
    const view = this.getExternalView();
    this.setState({
      externalView: {
        ...view,
        daysBack: view.daysBack + EXTERNAL_DAYS_PER_LOAD,
      },
    });
  };

  attachScrollListener = () => {
    if (typeof document === 'undefined' || this.scrollListenerAttached) {
      return;
    }
    document.addEventListener('scroll', this.handleScroll);
    this.scrollListenerAttached = true;
  };

  detachScrollListener = () => {
    if (typeof document === 'undefined' || !this.scrollListenerAttached) {
      return;
    }
    document.removeEventListener('scroll', this.handleScroll);
    this.scrollListenerAttached = false;
  };

  attachFilterSheetKeyListener = () => {
    if (
      typeof document === 'undefined' ||
      this.filterSheetKeyListenerAttached
    ) {
      return;
    }
    document.addEventListener('keydown', this.handleFilterSheetKeyDown);
    this.filterSheetKeyListenerAttached = true;
  };

  detachFilterSheetKeyListener = () => {
    if (
      typeof document === 'undefined' ||
      !this.filterSheetKeyListenerAttached
    ) {
      return;
    }
    document.removeEventListener('keydown', this.handleFilterSheetKeyDown);
    this.filterSheetKeyListenerAttached = false;
  };

  resetAutoLoadWindow = (fromPage = 1) => {
    this.setState({
      initialPage: fromPage,
      autoLoadUntilPage: fromPage + AUTO_LOAD_BATCH_WINDOW - 1,
      scrollAutoLoadEnabled: true,
    });
    this.lastSyncedPage = fromPage;
    this.attachScrollListener();
  };

  updateBatchUrl = (page) => {
    if (
      typeof window === 'undefined' ||
      !window.history ||
      !window.history.replaceState
    ) {
      return;
    }

    // Cap address-bar page at MAX_URL_PAGE; loading past that still works.
    const urlPage = clampJobsPage(page);

    const { pathname, search } = this.props.location;
    const nextUrl = buildJobsBatchUrl(pathname, search, urlPage);
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(
        { ...window.history.state, cameFromBackButton: false },
        document.title,
        nextUrl
      );
    }

    this.lastSyncedPage = urlPage;
  };

  syncBatchUrlAndAutoLoad = () => {
    const pagesLoaded = this.getPagesLoaded();
    if (pagesLoaded < 1) {
      return;
    }

    this.updateBatchUrl(pagesLoaded);

    if (pagesLoaded >= this.state.autoLoadUntilPage) {
      this.detachScrollListener();
      if (this.state.scrollAutoLoadEnabled) {
        this.setState({ scrollAutoLoadEnabled: false });
      }
    }
  };

  getPageType = () => {
    if (this.props.match.params.role) return 'role';
    if (this.props.match.params.country) return 'country';
    return 'jobs';
  };

  // Crisp (production only) floats above the sheet and covers bottom toggles.
  setCrispChatVisible = (visible) => {
    if (typeof window === 'undefined' || !window.$crisp) {
      return;
    }
    window.$crisp.push(['do', visible ? 'chat:show' : 'chat:hide']);
  };

  setFilterSheetOpen = (open) => {
    if (open) {
      if (typeof document !== 'undefined') {
        this.filterSheetTriggerEl = document.activeElement;
        document.body.style.overflow = 'hidden';
      }
      this.setCrispChatVisible(false);
      this.setState({ showFilterSheet: true }, () => {
        const titleEl =
          this.sheetTitleRef ||
          (typeof document !== 'undefined' &&
            document.getElementById('jobs-filter-sheet-title'));
        if (titleEl && typeof titleEl.focus === 'function') {
          titleEl.focus();
        }
      });
      return;
    }

    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    this.setCrispChatVisible(true);
    this.setState({ showFilterSheet: false }, () => {
      const trigger = this.filterSheetTriggerEl;
      this.filterSheetTriggerEl = null;
      if (trigger && typeof trigger.focus === 'function') {
        trigger.focus();
      }
    });
  };

  handleFilterSheetKeyDown = (event) => {
    if (!this.state.showFilterSheet) {
      return;
    }

    // Nested Choose roles dialog owns Escape / focus trap while open
    if (
      typeof document !== 'undefined' &&
      typeof document.querySelector === 'function' &&
      document.querySelector('[data-testid="choose-roles-dialog"]')
    ) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.setFilterSheetOpen(false);
      return;
    }

    if (event.key !== 'Tab' || typeof document === 'undefined') {
      return;
    }

    const sheet = document.querySelector('[data-testid="jobs-filter-sheet"]');
    if (!sheet) {
      return;
    }

    const focusable = Array.from(
      sheet.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Guests cannot apply filters or search — send them to seller signup.
   * @returns {boolean} true when the caller should abort
   */
  redirectGuestToSellerSignup = () => {
    if (this.props.user) {
      return false;
    }
    this.props.history.push('/signup?type=seller');
    return true;
  };

  // Filter change listeners
  onSelectedRoleChange = (selectedRole) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;

    filter.type = 'role';
    filter.selectedRole = selectedRole;

    this.setState({ filter });
    this.handleRoleFilterChange();
  };

  onRolesSelect = (selectedRoles, featuredRoles = false) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;

    const selectedRolesValues = featuredRoles
      ? selectedRoles
      : selectedRoles.map((item) => item.value);

    filter.selectedRoles = selectedRolesValues;

    // Drop the path role when it is no longer among the selected ticks
    const pathRoleName =
      (filter.selectedRole && filter.selectedRole.name) || filter.name || '';
    if (pathRoleName) {
      const normalize = (value) =>
        (value || '').toString().toLowerCase().replace(/-/g, ' ').trim();
      const pathKey = normalize(pathRoleName);
      const pathPlural = normalize(toPlural(pathRoleName));
      const stillSelected = selectedRolesValues.some((role) => {
        const key = normalize(role);
        return key === pathKey || key === pathPlural;
      });
      if (!stillSelected) {
        filter.name = '';
        filter.selectedRole = null;
      }
    }

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onRolesHaveChanged = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;
    filter.rolesHaveChanged = true;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onRoleToggle = (roleName) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const selected = [...(this.state.filter.selectedRoles || [])];
    const normalize = (value) =>
      (value || '').toString().toLowerCase().replace(/-/g, ' ').trim();
    const target = normalize(roleName);
    const pluralTarget = normalize(toPlural(roleName));
    const index = selected.findIndex((role) => {
      const key = normalize(role);
      return key === target || key === pluralTarget;
    });

    if (index >= 0) {
      selected.splice(index, 1);
    } else {
      selected.push(roleName);
    }

    const { filter } = this.state;
    filter.rolesHaveChanged = true;
    this.onRolesSelect(selected, true);
  };

  /**
   * Batch-apply a pending role selection (Choose roles dialog).
   * Same URL/refetch path as toggling roles in the column one by one.
   * No-op when the selection is unchanged so we do not refetch.
   */
  onRolesApply = (selectedRoles) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const current = this.state.filter.selectedRoles || [];
    const normalize = (value) =>
      (value || '').toString().toLowerCase().replace(/-/g, ' ').trim();
    const currentKeys = new Set(current.map(normalize));
    const nextKeys = new Set((selectedRoles || []).map(normalize));
    if (
      currentKeys.size === nextKeys.size &&
      [...currentKeys].every((key) => nextKeys.has(key))
    ) {
      return;
    }

    const { filter } = this.state;
    filter.rolesHaveChanged = true;
    this.onRolesSelect(selectedRoles || [], true);
  };

  onExperienceGroupToggle = (option) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const experienceLevels = toggleExperienceGroup(
      this.state.filter.experienceLevels,
      option
    );

    this.setState(
      (prevState) => ({
        filter: {
          ...prevState.filter,
          experienceLevels,
        },
        experienceLevelsHaveChanged: true,
      }),
      () => this.handleFilterChanges()
    );
  };

  onHiringCapacityToggle = (hiringCapacity) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const hiringCapacities = this.state.filter.hiringCapacities.map((item) =>
      item.type === hiringCapacity.type
        ? { ...item, isChecked: !item.isChecked }
        : item
    );

    this.setState(
      (prevState) => ({
        filter: {
          ...prevState.filter,
          hiringCapacities,
        },
        hiringCapacitiesHaveChanged: true,
      }),
      () => this.handleFilterChanges()
    );
  };

  onStatusFilterToggle = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;
    filter.status = !filter.status;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onRemoteFilterToggle = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;
    filter.remote = !filter.remote;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onInternalFilterToggle = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;
    filter.internal = !filter.internal;

    this.setState({ filter });
    this.handleFilterChanges();
  };

  applyLocationChange = (newLocation) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;

    if (!newLocation.country && !newLocation.city) {
      filter.locationText = null;
      filter.country = null;
      filter.city = null;
    } else {
      filter.locationText =
        newLocation.city === ''
          ? `${toSeoUrl(newLocation.country)}`
          : `${toSeoUrl(newLocation.city)}, ${toSeoUrl(newLocation.country)}`;
      filter.country = newLocation.country;
      filter.city = newLocation.city;
    }

    this.setState({ filter });
    this.handleFilterChanges();
  };

  onLocationTextChange = (newLocation) => {
    clearTimeout(this.locationDebounceTimer);
    this.locationDebounceTimer = setTimeout(() => {
      this.applyLocationChange(newLocation);
    }, FILTER_INPUT_DEBOUNCE_MS);
  };

  onSearchTermChange = (newSearchTerm) => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;

    filter.searchTerm = newSearchTerm || null;

    this.setState({ filter, searchTermInput: newSearchTerm || '' });
    this.handleFilterChanges();
  };

  onSearchTermInputChange = (event) => {
    this.setState({ searchTermInput: event.target.value });
  };

  onSearchSubmit = (event) => {
    event.preventDefault();
    if (this.redirectGuestToSellerSignup()) {
      return;
    }
    this.onSearchTermChange(this.state.searchTermInput);
  };

  buildClearedFilter = (overrides = {}) => ({
    ...this.state.filter,
    name: '',
    selectedRole: null,
    selectedRoles: [],
    status: true,
    remote: false,
    internal: false,
    locationText: null,
    country: null,
    city: null,
    searchTerm: null,
    rolesHaveChanged: true,
    experienceLevels: DEFAULT_EXPERIENCE_LEVELS.map((level) => ({
      ...level,
    })),
    hiringCapacities: [
      { type: 'commercial', isChecked: false, label: 'Commercial' },
      { type: 'personal', isChecked: false, label: 'Personal' },
    ],
    ...overrides,
  });

  onFiltersReset = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const filter = this.buildClearedFilter();
    this.resetAutoLoadWindow(1);
    this.setState(
      {
        filter,
        searchTermInput: '',
      },
      () => {
        clearJobsFilters(this.props.history);
      }
    );
  };

  /**
   * Role breadcrumb on role+location pages — keep only the URL role filter.
   */
  onRoleBreadcrumbClick = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const role = this.props.match.params.role;
    if (!role) {
      this.onFiltersReset();
      return;
    }

    const selectedRole = this.getCurrentSelectedRole({ name: role });
    const roleName =
      (selectedRole && selectedRole.name) || fromSeoUrl(role) || role;

    const filter = this.buildClearedFilter({
      name: role,
      selectedRole: selectedRole || null,
      selectedRoles: [roleName],
    });

    this.resetAutoLoadWindow(1);
    this.setState(
      {
        filter,
        searchTermInput: '',
      },
      () => {
        clearJobsFilters(this.props.history, cookie, `/jobs/${role}`);
      }
    );
  };

  isBestMatch = () =>
    Boolean(this.props.user) &&
    queryString.parse(this.props.location.search).order === BEST_MATCH_ORDER;

  onBestMatchNavigate = () => {
    if (!this.props.user) {
      this.props.history.push('/ari');
      return;
    }
    this.setOrder(BEST_MATCH_ORDER);
  };

  onNewestFirstSelect = () => {
    this.setOrder(null);
  };

  setOrder = (order) => {
    const { history, location } = this.props;
    if (this.state.showFilterSheet) {
      this.setFilterSheetOpen(false);
    }
    history.push(buildJobsOrderUrl(location.pathname, location.search, order));
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0 });
    }
  };

  onEmptyFocusChipKeyConsumed = () => {
    if (this.state.emptyFocusChipKey) {
      this.setState({ emptyFocusChipKey: null });
    }
  };

  /**
   * Empty-state chip × — same remove path as the filter column for that filter.
   */
  onEmptyRemoveChip = (chip, nextFocusKey) => {
    this.setState({ emptyFocusChipKey: nextFocusKey || null });

    switch (chip.type) {
      case 'search':
        this.onSearchTermChange('');
        break;
      case 'role':
        this.onRoleToggle(
          (chip.payload && (chip.payload.selected || chip.payload.roleName)) ||
            chip.label
        );
        break;
      case 'location':
        this.applyLocationChange({ country: null, city: null });
        break;
      case 'remote':
        this.onRemoteFilterToggle();
        break;
      case 'experience':
        if (chip.payload && chip.payload.option) {
          this.onExperienceGroupToggle(chip.payload.option);
        }
        break;
      case 'type':
        if (chip.payload && chip.payload.capacity) {
          this.onHiringCapacityToggle(chip.payload.capacity);
        }
        break;
      case 'internal':
        this.onInternalFilterToggle();
        break;
      default:
        break;
    }
  };

  getFilterPanelProps = () => {
    const { filter } = this.state;
    const { user } = this.props;

    return {
      filter,
      suggestedRoles: this.getSuggestedRoles().toJS(),
      featuredRoles: (user && user.featured_roles) || [],
      pageType: this.getPageType(),
      auth: user ? 'logged_in' : 'guest',
      filterCount: getAppliedFilterCount(filter),
      onRoleToggle: this.onRoleToggle,
      onRolesApply: this.onRolesApply,
      onExperienceGroupToggle: this.onExperienceGroupToggle,
      onHiringCapacityToggle: this.onHiringCapacityToggle,
      onRemoteFilterToggle: this.onRemoteFilterToggle,
      onInternalFilterToggle: this.onInternalFilterToggle,
      onStatusFilterToggle: this.onStatusFilterToggle,
      onLocationTextChange: this.onLocationTextChange,
      onLocationChange: this.applyLocationChange,
      onFiltersReset: this.onFiltersReset,
      onBestMatchNavigate: this.onBestMatchNavigate,
      onNewestFirstSelect: this.onNewestFirstSelect,
      bestMatchActive: this.isBestMatch(),
    };
  };

  // Hash helper function
  getHash = (nextState) => {
    const { paid, filter } = nextState || this.state;

    let rolesString = null;

    if (typeof filter.selectedRoles === 'object') {
      rolesString = filter.selectedRoles.join();
    } else if (typeof filter.selectedRoles === 'string') {
      rolesString = filter.selectedRoles;
    }

    const selectedExperienceLevels = filter.experienceLevels.filter(
      (l) => l.isChecked
    );
    const selectedExperienceLevelsString =
      selectedExperienceLevels.length > 0
        ? selectedExperienceLevels.map((l) => l.type).join()
        : null;

    const selectedHiringCapacities = filter.hiringCapacities.filter(
      (h) => h.isChecked
    );
    const selectedHiringCapacitiesString =
      selectedHiringCapacities.length > 0
        ? selectedHiringCapacities.map((h) => h.type).join()
        : null;

    const status = filter && filter.status ? filter.status : false;
    const remote = filter && filter.remote ? filter.remote : false;
    const internal = filter && filter.internal ? filter.internal : false;

    const jobLocationText =
      filter && filter.locationText ? filter.locationText : null;

    const searchTerm = filter && filter.searchTerm ? filter.searchTerm : null;

    return encodeURIComponent(
      `${paid}|${rolesString}|${selectedExperienceLevelsString}|${selectedHiringCapacitiesString}|${status}|${remote}|${internal}|${jobLocationText}|${searchTerm}`
    );
  };

  // Redux Getters
  getFilterRoleSkills = () =>
    this.props.projects.getIn(
      ['filterRoleSkills', this.state.paid.toString(), 'roleSkills'],
      List()
    );

  getFilterRoles = () =>
    this.props.projects
      .getIn(['filterRoles', this.state.paid.toString(), 'roles'], List())
      .map((id) => this.props.roleEntities.get(id.toString()).toJS());

  getFilterSkills = () =>
    this.props.projects
      .getIn(['filterSkills', this.state.paid.toString(), 'skills'], List())
      .map((id) => this.props.skillEntities.get(id.toString()).toJS());

  getSuggestedRoles = () =>
    this.props.suggestedRoles
      .get('suggestedRoles', List())
      .map((id) => this.props.roleEntities.get(id.toString()).toJS());

  getSuggestedSkills = () =>
    this.props.suggestedSkills
      .get('suggestedSkills', List())
      .map((id) => this.props.skillEntities.get(id.toString()).toJS());

  // Role Filter
  getCurrentSelectedRole = (filter = null) => {
    let selectedRole = filter || this.state.filter;

    if (filter !== null && typeof filter.slug_plural === 'undefined') {
      // Helper function that updates `selectedRole` if a match is found
      const matchRoleSkill = (filter, roleSkill) => {
        if (
          filter.name.toLowerCase() === roleSkill.name.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.name_plural.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.slug.toLowerCase() ||
          filter.name.toLowerCase() === roleSkill.slug_plural.toLowerCase()
        ) {
          selectedRole = roleSkill;
        }
      };

      // Search in FilterRoleSkills first
      this.getFilterRoleSkills()
        .toArray()
        .forEach((roleSkills) => {
          roleSkills.forEach((rs) => matchRoleSkill(filter, rs.toObject()));
        });

      // Then in SuggestedRoles
      this.getSuggestedRoles().forEach((role) => {
        matchRoleSkill(filter, role);
      });

      // Finally, search within the SuggestedSkills
      this.getSuggestedSkills().forEach((skill) => {
        matchRoleSkill(filter, skill);
      });
    }

    return selectedRole;
  };

  getSelectedRoleName = () => {
    const currentSelectedRole = this.getCurrentSelectedRole();

    if (!currentSelectedRole || !currentSelectedRole.name) {
      return '';
    }

    let currentFilterName = currentSelectedRole.name;
    currentFilterName = currentFilterName.replace('-', ' ');
    currentFilterName = currentFilterName.replace('-', ' ');
    currentFilterName = currentFilterName.replace('-', ' ');

    currentFilterName = currentFilterName
      .toLowerCase()
      .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

    currentFilterName = currentFilterName.replace('3d ', '3D ');
    currentFilterName = currentFilterName.replace('Ui ', 'UI ');
    currentFilterName = currentFilterName.replace('Ux ', 'UX ');
    currentFilterName = currentFilterName.replace('Dj', 'DJ');
    currentFilterName = currentFilterName.replace('Front End ', 'Front-end ');
    currentFilterName = currentFilterName.replace('Back End ', 'Back-end ');
    return currentFilterName;
  };

  parseRoles = () => {
    const filteredRoles = [];
    const parsedRoles = queryString.parse(this.props.location.search).roles;

    const { selectedRole } = this.state.filter;

    if (typeof parsedRoles === 'string') {
      filteredRoles.push(parsedRoles);
    } else if (typeof parsedRoles === 'object') {
      parsedRoles.forEach((r) => filteredRoles.push(r));
    }

    if (
      this.props.match.params.role &&
      !filteredRoles.includes(selectedRole.name)
    ) {
      filteredRoles.push(selectedRole.name);
    }

    return filteredRoles ?? [];
  };

  // Parses query string and returns experience levels array
  parseFilteredExperienceLevels = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    // Parse experience levels from query string
    const filteredExperienceLevels = [];
    const parsedExperienceLevels = queryString.parse(search).experienceLevel;

    if (typeof parsedExperienceLevels === 'string') {
      // 1 experience level filtered - represented as string
      filteredExperienceLevels.push(parsedExperienceLevels);
    } else if (typeof parsedExperienceLevels === 'object') {
      // multiple experience levels filtered - represented as string[]
      parsedExperienceLevels.forEach((l) => filteredExperienceLevels.push(l));
    }

    // Update `isChecked` for the experience levels found in the query string
    const stateExperienceLevels = this.state.filter.experienceLevels;

    filteredExperienceLevels.forEach((l) => {
      const parsedExperienceLevel = stateExperienceLevels.find(
        (el) => el.type === l
      );

      if (typeof parsedExperienceLevel !== 'undefined') {
        parsedExperienceLevel.isChecked = true;
      }
    });

    // Experience levels array with updated `isChecked` fields
    return stateExperienceLevels;
  };

  // Parses query string and returns hiring capacities array
  parseFilteredHiringCapacities = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    // Parse hiring capacities from query string
    const filteredHiringCapacities = [];
    const parsedHiringCapacities = queryString.parse(search).hiringCapacity;

    if (typeof parsedHiringCapacities === 'string') {
      // 1 hiring capacity filtered - represented as string
      filteredHiringCapacities.push(parsedHiringCapacities);
    } else if (typeof parsedHiringCapacities === 'object') {
      // multiple hiring capacities filtered - represented as string[]
      parsedHiringCapacities.forEach((h) => filteredHiringCapacities.push(h));
    }

    // Update `isChecked` for the hiring capacities found in the query string
    const stateHiringCapacities = this.state.filter.hiringCapacities;

    filteredHiringCapacities.forEach((h) => {
      const parsedHiringCapacity = stateHiringCapacities.find(
        (hc) => hc.type === h
      );

      if (typeof parsedHiringCapacity !== 'undefined') {
        parsedHiringCapacity.isChecked = true;
      }
    });

    // Hiring Capacities array with updated `isChecked` fields
    return stateHiringCapacities;
  };

  // Parse Job Status Open filter (default ON when absent)
  parsestatus = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { status } = queryString.parse(search);

    return parseJobsStatus(status);
  };

  // Parse Roles Have Changed Job filter
  parseRolesHaveChanged = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { rolesHaveChanged } = queryString.parse(search);

    return !!rolesHaveChanged;
  };

  // Parse Remote Job filter
  parseRemote = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { remote } = queryString.parse(search);

    return !!remote;
  };

  // // Parse easyApply Job filter
  parseInternal = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { internal } = queryString.parse(search);

    return !!internal;
  };

  parseSearchTerm = (locationSearch = null) => {
    const search =
      locationSearch !== null ? locationSearch : this.props.location.search;

    const { searchTerm } = queryString.parse(search);

    return searchTerm;
  };

  // Other Handlers
  handleScroll = () => {
    if (!this.state.scrollAutoLoadEnabled) {
      return;
    }

    const listings = document.getElementById('brief-listings');
    if (!listings) {
      return;
    }

    const distanceFromBottom =
      listings.getBoundingClientRect().bottom - window.innerHeight;

    if (distanceFromBottom < 450) {
      this.loadMore();
    }
  };

  handleLoadMoreClick = (event) => {
    event.preventDefault();
    this.loadMore();
  };

  handleShowMoreJobs = (event) => {
    if (event) {
      event.preventDefault();
    }
    const pagesLoaded = this.getPagesLoaded();
    this.setState({
      autoLoadUntilPage: pagesLoaded + AUTO_LOAD_BATCH_WINDOW,
      scrollAutoLoadEnabled: true,
    });
    this.attachScrollListener();
    this.loadMore();
  };

  handleAddBrief = (overview, description, role) =>
    this.props.postBrief(overview, description, role);

  // Filter Change Handler
  handleFilterChanges = () => {
    if (this.redirectGuestToSellerSignup()) {
      return;
    }

    const { filter } = this.state;
    const experienceLevels = this.state.filter.experienceLevels;
    const hiringCapacities = this.state.filter.hiringCapacities;

    this.updateCookie(filter);

    // Role Filter
    const roleFilter = { name: filter.name, selectedRole: filter.selectedRole };
    // Experience Level Filter
    const experienceLevelFilter = experienceLevels.filter((l) => l.isChecked);
    // Hiring Capacity Filter
    const hiringCapacityFilter = hiringCapacities.filter((h) => h.isChecked);

    // Generate redirection URL
    let newPathname = '/jobs';

    // Role Filter
    if (roleFilter.selectedRole) {
      if (roleFilter.selectedRole.slug_plural === undefined) {
        newPathname += `/${toPlural(
          roleFilter.selectedRole.name.toLowerCase().replace(' ', '-')
        )}`;
      } else {
        newPathname += `/${roleFilter.selectedRole.slug_plural}`;
      }
    }

    if (filter.locationText) {
      newPathname += filter.city
        ? `/in/${toSeoUrl(filter.country)}/${toSeoUrl(filter.city)}`
        : `/in/${toSeoUrl(filter.country)}`;
    }
    // Query string — filter changes always reset to page 1 (omit page param)
    const query = {};

    if (filter.selectedRoles.length !== 0) {
      query.roles = filter.selectedRoles;
    }

    // Experience Level Filter
    if (experienceLevelFilter.length !== 0) {
      query.experienceLevel = experienceLevelFilter.map((l) => l.type);
    }

    // Hiring Capacity Filter
    if (hiringCapacityFilter.length !== 0) {
      query.hiringCapacity = hiringCapacityFilter.map((h) => h.type);
    }

    // Job Status Open Filter (explicit false so refresh does not snap back on)
    query.status = filter.status ? true : false;

    if (filter.internal) {
      query.internal = true;
    }

    // Remote Job Filter
    if (filter.remote) {
      query.remote = 1;
    }

    // Remote Job Filter
    if (filter.rolesHaveChanged) {
      query.rolesHaveChanged = 1;
    }

    if (filter.searchTerm) {
      query.searchTerm = filter.searchTerm;
    }

    this.resetAutoLoadWindow(1);

    this.props.history.push({
      pathname: newPathname,
      search: queryString.stringifyUrl({ url: '', query }),
    });
  };

  // Role Filter Change Handler
  handleRoleFilterChange = (filter = null) => {
    // Get filter role based on current filter
    const currentFilter =
      filter === null ? this.state.filter.selectedRole : filter;
    const currentRoleSkill = this.getCurrentSelectedRole(currentFilter);

    // Update existing Role Filter within state
    const newFilter = this.state.filter;
    newFilter.selectedRole = currentRoleSkill;
    newFilter.name = currentRoleSkill.slug_plural;
    newFilter.type = currentRoleSkill.type;

    this.setState({ filter: newFilter });

    const filterHasChanged =
      (typeof currentRoleSkill.slug_plural !== 'undefined' &&
        this.props.match.params.role &&
        this.props.match.params.role !== currentRoleSkill.slug_plural) ||
      !this.props.match.params.role;

    if (filterHasChanged) {
      this.handleFilterChanges();
    }
  };

  // Redux Loaders
  loadSuggestedRoles = () => {
    if (
      !this.props.suggestedRoles.get('loaded', false) &&
      !this.props.suggestedRoles.get('loading', false)
    ) {
      this.props.loadSuggestedRoles();
    }
  };

  loadSuggestedSkills = () => {
    if (
      !this.props.suggestedSkills.get('loaded', false) &&
      !this.props.suggestedSkills.get('loading', false)
    ) {
      this.props.loadSuggestedSkills();
    }
  };

  getPagesLoaded = () => {
    const hash = this.getHash();

    return this.props.projects.getIn(['briefs', hash, 'pagesLoaded'], 0);
  };

  loadMore = () => {
    const hash = this.getHash();

    if (this.hasMoreExternal()) {
      this.revealMoreExternalDays();
      return;
    }

    if (
      this.props.projects.getIn(['briefs', hash, 'moreToLoad'], false) &&
      !this.props.projects.getIn(['briefs', hash, 'loading'], false)
    ) {
      this.props.loadBriefs(
        this.props.projects.getIn(['briefs', hash, 'pagesLoaded'], 0) + 1,
        this.state.paid,
        this.state.filter.selectedRoles,
        this.state.filter.experienceLevels,
        this.state.filter.hiringCapacities,
        this.state.filter.status,
        this.state.filter.remote,
        this.state.filter.internal,
        this.state.filter.locationText,
        this.state.filter.searchTerm
      );
    }
  };

  UNSAFE_componentWillMount() {
    const { filter } = this.state;

    // Read initial Role Filter, based on URL slug
    const roleSkillParam = this.props.match.params.role;

    const locationParam = this.props.match.params.city
      ? `${this.props.match.params.city}, ${this.props.match.params.country}`
      : this.props.match.params.country;

    if (roleSkillParam) {
      filter.selectedRole = this.getCurrentSelectedRole({
        name: roleSkillParam,
      });
    }

    // Read initial Roles Filters, based on query string
    filter.selectedRoles = this.parseRoles();

    // Read initial Experience Level Filter, based on query string
    const experienceLevels = this.parseFilteredExperienceLevels();

    // Read initial Hiring Capacity Filter, based on query string
    const hiringCapacities = this.parseFilteredHiringCapacities();

    // Read initial Job State Open Filter, based on query string
    filter.status = this.parsestatus();

    // Read initial Job State Open Filter, based on query string
    filter.remote = this.parseRemote();

    // Read initial Roles Have Changed, based on query string
    filter.rolesHaveChanged = this.parseRolesHaveChanged();

    filter.internal = this.parseInternal();

    // Read initial Job Location Text Filter, based on query string
    filter.locationText = locationParam;

    filter.searchTerm = this.parseSearchTerm();

    // Update state with new values for RoleFilter, ExperienceLevelFilter and HiringCapacityFilter
    this.setState({
      filter,
      experienceLevels,
      hiringCapacities,
      searchTermInput: filter.searchTerm || '',
    });
  }

  UNSAFE_componentWillUpdate(nextProps, nextState) {
    const newState = {};

    // Update Role Filter, on URL slug change
    if (nextProps.match.params.role !== this.props.match.params.role) {
      if (nextProps.match.params.role) {
        newState.filter = {
          ...nextState.filter,
          name: nextProps.match.params.role,
          selectedRole: this.getCurrentSelectedRole({
            name: nextProps.match.params.role,
          }),
          selectedRoles: [
            this.getCurrentSelectedRole({
              name: nextProps.match.params.role,
            }).name,
          ],
        };
      } else {
        newState.filter = {
          ...nextState.filter,
          name: '',
          selectedRole: null,
          selectedRoles: [],
        };
      }
    }

    // Filter flags set by checkbox handlers — do not also treat every
    // location.search rewrite from handleFilterChanges as a fresh change
    // (that previously stacked duplicate /notices/collaboration requests).
    const experienceLevelsChanged = nextState.experienceLevelsHaveChanged;
    const hiringCapacitiesChanged = nextState.hiringCapacitiesHaveChanged;

    // Update `paid` if navigated to collaborate or jobs
    if (
      nextProps.location.pathname.includes('/jobs') !==
      this.props.location.pathname.includes('/jobs')
    ) {
      newState.paid = nextProps.location.pathname.includes('/jobs');
    }

    // Load RoleSkills if `paid` changed
    if (nextState.paid !== this.state.paid) {
      if (
        !this.props.projects.getIn(
          ['filterRoleSkills', this.state.paid.toString(), 'loaded'],
          false
        ) &&
        !this.props.projects.getIn(
          ['filterRoleSkills', this.state.paid.toString(), 'loading'],
          false
        )
      ) {
        this.props.loadFilterRoleSkills(this.state.paid);
      }
    }

    // Reload Briefs - if `paid`, `filter`, `experienceLevels` or `hiringCapacities` have changed
    const paidChanged = nextState.paid !== this.state.paid;
    const roleSkillChanged = nextState.filter !== this.state.filter;
    const rolesChanged =
      nextState.filter.selectedRoles !== this.state.filter.selectedRoles;
    const jobStatusFilterChanged =
      nextState.filter.status !== this.state.filter.status;
    const remoteFilterChanged =
      nextState.filter.remote !== this.state.filter.remote;
    const internalFilterChanged =
      nextState.filter.internal !== this.state.filter.internal;
    const jobLocationTextChanged =
      nextState.filter.locationText !== this.state.filter.locationText;
    const jobSearchTermChanged =
      nextState.filter.searchTerm !== this.state.filter.searchTerm;

    if (
      paidChanged ||
      roleSkillChanged ||
      rolesChanged ||
      experienceLevelsChanged ||
      hiringCapacitiesChanged ||
      jobStatusFilterChanged ||
      remoteFilterChanged ||
      internalFilterChanged ||
      jobLocationTextChanged ||
      jobSearchTermChanged
    ) {
      // Reset experience / hiring change flags (correct state keys)
      newState.experienceLevelsHaveChanged = false;
      newState.hiringCapacitiesHaveChanged = false;

      // Decide whether to re-load briefs

      const briefsHaveLoaded = this.props.projects.getIn(
        ['briefs', this.getHash(nextState), 'loaded'],
        false
      );
      const briefsAreLoading = this.props.projects.getIn(
        ['briefs', this.getHash(nextState), 'loading'],
        false
      );

      if (rolesChanged) {
        if (
          nextState.filter.selectedRoles &&
          nextState.filter.selectedRoles.length > 0
        ) {
          this.props.setSelectedRoleCategory(
            getRoleCategory(nextState.filter.selectedRoles[0])
          );
        } else {
          this.props.clearSelectedRoleCategory();
        }
      }

      if (!briefsHaveLoaded && !briefsAreLoading) {
        // Filter changes always reload from page 1
        this.props.loadBriefs(
          1,
          nextState.paid,
          nextState.filter.selectedRoles,
          nextState.filter.experienceLevels,
          nextState.filter.hiringCapacities,
          nextState.filter.status,
          nextState.filter.remote,
          nextState.filter.internal,
          nextState.filter.locationText,
          nextState.filter.searchTerm
        );
      }
    }

    if (!this.props.posted && nextProps.posted) {
      this.props.history.push(nextProps.posted.links.ats_main_relative);
    }

    // Update State
    if (Object.keys(newState).length !== 0) {
      this.setState(newState);
    }
  }

  getMoreToLoad = () => {
    const hash = this.getHash();
    return this.props.projects.getIn(['briefs', hash, 'moreToLoad'], false);
  };

  render() {
    const {
      projects,
      briefsEntities,
      userEntities,
      user,
      partners,
      loadedPartners,
      loadingPartners,
      location,
    } = this.props;

    const {
      paid,
      filter,
      showFilterSheet,
      searchTermInput,
      scrollAutoLoadEnabled,
    } = this.state;

    const filterCount = getAppliedFilterCount(filter);
    const bestMatch = this.isBestMatch();
    const externalFailed = paid && !bestMatch && this.hasExternalLoadFailed();
    const pageType = this.getPageType();
    const auth = user ? 'logged_in' : 'guest';
    const filterPanelProps = this.getFilterPanelProps();
    const filterButtonLabel =
      filterCount > 0
        ? `Filter and order · ${filterCount}`
        : 'Filter and order';

    const pagesLoaded = this.getPagesLoaded();
    const moreToLoad = this.getMoreToLoad();
    const currentBatchPage =
      pagesLoaded > 0 ? pagesLoaded : this.state.initialPage;
    // Crawlable href never advertises page > MAX_URL_PAGE
    const loadMoreHrefPage =
      currentBatchPage < MAX_URL_PAGE ? currentBatchPage + 1 : MAX_URL_PAGE;
    const loadMoreHref = buildJobsBatchUrl(
      location.pathname,
      location.search,
      loadMoreHrefPage
    );
    const moreExternal = !bestMatch && this.hasMoreExternal();
    const showLoadMoreRow = moreToLoad || moreExternal;
    const showShowMoreButton = showLoadMoreRow && !scrollAutoLoadEnabled;
    const isLoadingMore =
      projects.getIn(['briefs', this.getHash(), 'loading'], false) ||
      (moreExternal && this.isExternalLoading());

    const briefListings = projects
      .getIn(['briefs', this.getHash(), 'briefs'], List())
      .map((brief) => {
        const object = briefsEntities.get(brief.toString()).toJS();
        if (object) {
          object.user = userEntities.get(object.user.toString()).toJS();
        }

        return object;
      })
      .toJS();

    // Newest first: Twine jobs and jobs from other sites, grouped by day.
    const timelineTimeZone = this.state.timelineTimeZone;
    const timelineToday = dayKey(Date.now(), timelineTimeZone);
    const externalView = this.getExternalView();
    const externalEntry = this.getExternalEntry(timelineTimeZone);
    const timeline =
      paid && !bestMatch
        ? buildJobsTimeline({
            briefs: briefListings,
            externalDays: filter.internal
              ? {}
              : (externalEntry && externalEntry.days) || {},
            moreToLoad,
            externalDaysBack: externalView.daysBack,
            today: timelineToday,
            timeZone: timelineTimeZone,
          })
        : null;

    return (
      <div>
        <Grid>
          <ProjectsHeader
            config={config}
            filter={filter}
            getSelectedRoleName={this.getSelectedRoleName}
            loggedIn={user ? true : false}
            liveJobCount={projects.getIn(
              ['briefs', this.getHash(), 'total'],
              0
            )}
            batchPage={currentBatchPage}
            moreToLoad={moreToLoad}
            locationPathname={location.pathname}
            locationSearch={location.search}
            onJobsBreadcrumbClick={this.onFiltersReset}
            onRoleBreadcrumbClick={this.onRoleBreadcrumbClick}
          >
            <form
              className={layoutStyles.keywordSearch}
              onSubmit={this.onSearchSubmit}
              role="search"
            >
              <input
                type="search"
                className={layoutStyles.keywordInput}
                value={searchTermInput}
                placeholder="Search jobs, skills or clients"
                onChange={this.onSearchTermInputChange}
                maxLength={140}
                aria-label="Search jobs, skills or clients"
              />
              <button
                type="submit"
                className={layoutStyles.searchButton}
                data-testid="jobs-search-submit"
              >
                Search
              </button>
            </form>
          </ProjectsHeader>

          <div className={layoutStyles.layout}>
            <aside
              className={layoutStyles.desktopAsideColumn}
              aria-label="Filter and order"
            >
              <div className={layoutStyles.desktopAside}>
                <JobsFilterPanel
                  {...filterPanelProps}
                  idPrefix="desktop"
                  showPanelHeader
                />
              </div>
              {!user && (
                <BrowseMoreJobs
                  placement="desktop"
                  country={
                    (filter && filter.country) ||
                    this.props.match.params.country ||
                    null
                  }
                />
              )}
            </aside>

            <div className={layoutStyles.mainColumn}>
              <div className={layoutStyles.mobileBar}>
                {/* Ari has its own criteria (Edit criteria); only ordering applies. */}
                {!bestMatch && (
                  <button
                    type="button"
                    className={layoutStyles.filterButton}
                    onClick={() => this.setFilterSheetOpen(true)}
                    data-testid="jobs-filter-open"
                  >
                    {filterButtonLabel}
                  </button>
                )}
                <button
                  type="button"
                  className={layoutStyles.orderButton}
                  onClick={() => this.setFilterSheetOpen(true)}
                  data-testid="jobs-order-open"
                >
                  {bestMatch ? 'Best match' : 'Newest first'}
                  <ChevronDown size={16} aria-hidden="true" />
                </button>
              </div>

              {paid && (
                <JobsResultsHeader
                  bestMatch={bestMatch}
                  pageType={pageType}
                  filterCount={filterCount}
                  role={
                    (filter && filter.name) ||
                    this.props.match.params.role ||
                    null
                  }
                  selectedRoleName={this.getSelectedRoleName()}
                  country={
                    (filter && filter.country) ||
                    this.props.match.params.country ||
                    null
                  }
                  city={
                    (filter && filter.city) ||
                    this.props.match.params.city ||
                    null
                  }
                  auth={auth}
                  onBestMatchNavigate={this.onBestMatchNavigate}
                  filterSummary={getFilterSummary(filter)}
                />
              )}

              {externalFailed && (
                <div
                  className={layoutStyles.externalError}
                  role="alert"
                  data-testid="jobs-external-error"
                >
                  <span>
                    We couldn’t load jobs from other sites, so some days may
                    look empty.
                  </span>
                  <button
                    type="button"
                    className={layoutStyles.externalRetry}
                    onClick={this.onRetryExternal}
                    data-testid="jobs-external-retry"
                  >
                    Try again
                  </button>
                </div>
              )}

              {bestMatch ? (
                <Ari embedded />
              ) : (
                <>
                  <BriefListings
                    briefs={briefListings}
                    id="brief-listings"
                    loaded={projects.getIn(
                      ['briefs', this.getHash(), 'loaded'],
                      false
                    )}
                    loading={projects.getIn(
                      ['briefs', this.getHash(), 'loading'],
                      false
                    )}
                    selectedRole={this.getCurrentSelectedRole()}
                    paid={paid}
                    onClickRole={this.handleRoleFilterChange}
                    onClickLocation={this.applyLocationChange}
                    country={filter && filter.country ? filter.country : ''}
                    partners={partners}
                    loadedPartners={loadedPartners}
                    loadingPartners={loadingPartners}
                    pageType={pageType}
                    auth={auth}
                    filterCount={filterCount}
                    onBestMatchNavigate={this.onBestMatchNavigate}
                    showBestMatchCard={paid}
                    filter={filter}
                    suggestedRoles={this.getSuggestedRoles().toJS()}
                    pageRole={this.props.match.params.role || null}
                    pageCountry={this.props.match.params.country || null}
                    pageCity={this.props.match.params.city || null}
                    emptyFocusChipKey={this.state.emptyFocusChipKey}
                    onEmptyFocusChipKeyConsumed={
                      this.onEmptyFocusChipKeyConsumed
                    }
                    onEmptyRemoveChip={this.onEmptyRemoveChip}
                    onFiltersReset={this.onFiltersReset}
                    timeline={timeline}
                    timelineToday={timelineToday}
                    expandedExternalDays={externalView.expanded}
                    onShowAllExternal={this.onShowAllExternal}
                  />

                  {showLoadMoreRow && (
                    <div
                      className={layoutStyles.loadMoreRow}
                      data-testid="jobs-load-more-row"
                    >
                      {isLoadingMore ? (
                        <div
                          className={layoutStyles.loadMoreStatus}
                          aria-live="polite"
                          data-testid="jobs-load-more-status"
                        >
                          <span
                            className={layoutStyles.loadMoreSpinner}
                            aria-hidden="true"
                          />
                          <span>Loading more jobs</span>
                        </div>
                      ) : showShowMoreButton ? (
                        <a
                          href={loadMoreHref}
                          className={layoutStyles.showMoreButton}
                          onClick={this.handleShowMoreJobs}
                          data-testid="jobs-show-more"
                        >
                          Show more jobs
                        </a>
                      ) : (
                        <a
                          href={loadMoreHref}
                          className={layoutStyles.loadMoreLink}
                          onClick={this.handleLoadMoreClick}
                          data-testid="jobs-load-more"
                        >
                          Load more jobs
                        </a>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div
              className={`${layoutStyles.sheet} ${
                showFilterSheet ? layoutStyles.sheetOpen : ''
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="jobs-filter-sheet-title"
              aria-hidden={!showFilterSheet}
              data-testid="jobs-filter-sheet"
            >
              <div className={layoutStyles.sheetHeader}>
                <h2
                  id="jobs-filter-sheet-title"
                  className={layoutStyles.sheetTitle}
                  tabIndex={-1}
                  ref={(el) => {
                    this.sheetTitleRef = el;
                  }}
                >
                  Filter and order
                </h2>
                {!bestMatch && (
                  <button
                    type="button"
                    className={layoutStyles.sheetClearAll}
                    onClick={this.onFiltersReset}
                    disabled={filterCount === 0}
                    data-testid="jobs-filter-clear-all"
                  >
                    Clear all
                  </button>
                )}
                <button
                  type="button"
                  className={layoutStyles.sheetClose}
                  onClick={() => this.setFilterSheetOpen(false)}
                  aria-label="Close filters"
                  data-testid="jobs-filter-sheet-close"
                >
                  <X size={20} />
                </button>
              </div>
              <div className={layoutStyles.sheetBody}>
                <JobsFilterPanel
                  {...filterPanelProps}
                  idPrefix="sheet"
                  showPanelHeader={false}
                />
              </div>
            </div>
          </div>
        </Grid>
      </div>
    );
  }
}
